import { useCallback } from 'react';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import { TakerMarket } from './useTakerMarkets';

export type ClosedMarketCountMode = 'eligible' | 'total';

export interface ClosedMarketSummary {
  /** Whether any market in the provided list has `marketIsEnabled === false`. */
  hasClosedMarkets: boolean;
  /** Number of markets with `marketIsEnabled === false`. */
  closedMarketCount: number;
  /**
   * Sum of invoice counts for closed markets.
   * Uses `eligibleInvoiceCount` when countMode is 'eligible',
   * `totalInvoiceCount` when countMode is 'total'.
   */
  closedInvoiceCount: number;
  /**
   * Sum of invoice amounts for closed markets, converted to selectedCurrency.
   * Uses `eligibleInvoiceAmount` when countMode is 'eligible',
   * `totalInvoiceAmount` when countMode is 'total'.
   */
  closedInvoiceAmount: number;
}

/**
 * Resolves shared dependencies (exchange rates, selected currency) once and returns
 * a `getClosedMarkets` function that can be called per-buyer inside a `.map()`.
 *
 * A market is considered 'closed' when `marketIsEnabled === false`.
 * `marketIsInactive` is intentionally excluded — that condition reflects
 * invoice inactivity, not a buyer-disabled market closure.
 *
 * @example
 * const { getClosedMarkets } = useClosedMarkets();
 * // inside .map():
 * const { hasClosedMarkets, closedInvoiceAmount } = getClosedMarkets(buyer.takerMarkets, 'total');
 */
const useClosedMarkets = () => {
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();

  const getClosedMarkets = useCallback(
    (takerMarkets: TakerMarket[], countMode: ClosedMarketCountMode): ClosedMarketSummary => {
      const closedMarkets = takerMarkets.filter((tm) => !tm.marketIsEnabled);

      if (closedMarkets.length === 0) {
        return {
          hasClosedMarkets: false,
          closedMarketCount: 0,
          closedInvoiceCount: 0,
          closedInvoiceAmount: 0,
        };
      }

      let closedInvoiceCount = 0;
      let closedInvoiceAmount = 0;

      for (const tm of closedMarkets) {
        if (countMode === 'eligible') {
          closedInvoiceCount += tm.eligibleInvoiceCount;
          closedInvoiceAmount += convertCurrency({
            amount: tm.eligibleInvoiceAmount,
            exchangeRates,
            from: tm.currency,
            to: selectedCurrency,
          });
        } else {
          closedInvoiceCount += tm.totalInvoiceCount;
          closedInvoiceAmount += convertCurrency({
            amount: tm.totalInvoiceAmount,
            exchangeRates,
            from: tm.currency,
            to: selectedCurrency,
          });
        }
      }

      return {
        hasClosedMarkets: true,
        closedMarketCount: closedMarkets.length,
        closedInvoiceCount,
        closedInvoiceAmount,
      };
    },
    [exchangeRates, selectedCurrency]
  );

  return { getClosedMarkets };
};

export default useClosedMarkets;
